/**
 * Usage tips on Home (Sep 2026): at most one tip a day, picked from the tips
 * that fit this user and device, skipping features they already use.
 *
 * Everything is per browser in localStorage, per signed-in user:
 *   daaily.tips:<email>  { off, seen: string[], shownOn, id }
 *   daaily.used.<feature>  set by the feature itself (lib/usage.ts) — a tip about
 *                          something the user already does never shows.
 */
import { wasUsed as used } from '../lib/usage';

export type TipAction = { kind: 'route'; to: string } | { kind: 'palette' } | { kind: 'shortcuts' };
export interface Tip {
  id: string;
  title: string;
  body: string;
  action?: { label: string; do: TipAction };
  /** Show only when true (device, access, feature not used yet). */
  when?: (ctx: TipContext) => boolean;
}
export interface TipContext { driversEnabled: boolean; wide: boolean; phone: boolean }

const mod = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

export const TIPS: Tip[] = [
  {
    id: 'palette', title: `Press ${mod}K to jump anywhere`,
    body: 'Search every app, page, open to-do and, with the Org Chart, your colleagues. Enter opens the result.',
    action: { label: 'Try it', do: { kind: 'palette' } }, when: (c) => !c.phone && !used('palette'),
  },
  {
    id: 'shortcuts', title: 'Press ? for keyboard shortcuts',
    body: 'A short sheet lists every shortcut in the hub, like g then h for Home.',
    action: { label: 'Show shortcuts', do: { kind: 'shortcuts' } }, when: (c) => !c.phone && !used('shortcuts'),
  },
  {
    id: 'goto', title: 'Go places with two keys',
    body: 'Press g, then h for Home, a for Activity, d for DAAily Drivers or u for the Guide.',
    when: (c) => !c.phone && !used('goto'),
  },
  {
    id: 'guide', title: 'Ask the Guide',
    body: 'Every app has a short explanation in the Guide, and you can ask it questions in plain words.',
    action: { label: 'Open the Guide', do: { kind: 'route', to: '/guide' } }, when: () => !used('guide'),
  },
  {
    id: 'split', title: 'Two apps side by side',
    body: 'On a wide screen, the columns button in the top bar opens a second app next to the one you are in.',
    when: (c) => c.wide && !used('split'),
  },
  {
    id: 'feedback', title: 'Spotted a bug or have an idea?',
    body: 'The feedback button next to the bell sends it straight to the team, with an optional screenshot.',
  },
  {
    id: 'drivers-anon', title: 'Nominations can be anonymous',
    body: 'Uncheck "Show my name" and nobody sees who nominated, not even HR.',
    action: { label: 'Nominate someone', do: { kind: 'route', to: '/drivers/new' } }, when: (c) => c.driversEnabled,
  },
];

interface TipState { off?: boolean; seen?: string[]; shownOn?: string; id?: string; dismissedOn?: string }
const stateKey = (email: string) => `daaily.tips:${email.toLowerCase()}`;
function read(email: string): TipState {
  try { return JSON.parse(localStorage.getItem(stateKey(email)) || '{}') as TipState; } catch { return {}; }
}
function write(email: string, s: TipState) { try { localStorage.setItem(stateKey(email), JSON.stringify(s)); } catch { /* ignore */ } }
const localDay = () => new Date().toLocaleDateString('en-CA');

/** Today's tip, or null (tips off, dismissed today, or none left that fit). Stable for the day. */
export function tipForToday(email: string, ctx: TipContext): Tip | null {
  if (!email) return null;
  const s = read(email);
  const today = localDay();
  if (s.off || s.dismissedOn === today) return null;
  const fits = TIPS.filter((t) => !t.when || t.when(ctx));
  if (s.shownOn === today) {
    const same = fits.find((t) => t.id === s.id);
    if (same) return same;
  }
  const seen = new Set(s.seen ?? []);
  const next = fits.find((t) => !seen.has(t.id)) ?? null;
  if (!next) return null;
  write(email, { ...s, shownOn: today, id: next.id, seen: [...seen, next.id] });
  return next;
}

/** Hide tips until tomorrow. */
export function dismissTipForToday(email: string) { write(email, { ...read(email), dismissedOn: localDay() }); }
/** Turn tips off for good (per browser). */
export function turnTipsOff(email: string) { write(email, { ...read(email), off: true }); }
